import React, {ReactNode} from "react";

import cx from "classnames";

import LoadingMessage from "@components/LoadingMessage";
import MenuItems from "@components/MenuItems";

import styles from "./Menu.scss";
import MenuClosedMessage from "./MenuClosedMessage";
import {SocialProofConfigProvider} from "./SocialProof/SocialProofContext";
import useMenu from "./hooks/useMenu";
import {GrabNGoMenuParams, InviteMenuParams, UseMenuParams} from "./interfaces";

export type MenuProps = UseMenuParams & {
  isHidden?: boolean;
  hiddenMessage?: string;
  selectedSortOption?: number;
  footerContent?: ReactNode;
  headerContent?: ReactNode;
  containerClassName?: string;
  preSelectedItemSlug?: string;
};

export default function Menu({
  isHidden,
  hiddenMessage,
  selectedSortOption = 0,
  footerContent,
  headerContent,
  containerClassName,
  preSelectedItemSlug,
  ...params
}: MenuProps): JSX.Element {
  const {menu, isLoading, isError, error} = useMenu(params);
  const isHoliday = Boolean(menu?.holiday);
  const isClosed = Boolean(menu?.closed);
  const isBlackout = Boolean(menu?.blackout);
  const emptyMessage = menu?.sort_options?.[selectedSortOption]?.empty_message;

  const isNotAvailable = isError || isHoliday || isBlackout || isClosed;
  const isMenuShown = !isLoading && !isNotAvailable && !isHidden && menu;

  return (
    <SocialProofConfigProvider
      inviteKey={(params as InviteMenuParams)?.inviteKey}
      selectedDate={(params as GrabNGoMenuParams)?.selectedDate}
    >
      <div className={cx(styles.menuContent, containerClassName)}>
        {isMenuShown && headerContent && <header>{headerContent}</header>}
        <MenuClosedMessage
          menu={menu}
          isError={isError}
          error={error}
          isHidden={isHidden}
          hiddenMessage={hiddenMessage}
          isNotAvailable={isNotAvailable}
        />
        {emptyMessage && (
          <h4 className={styles.sortOptionEmptyMessage}>{emptyMessage}</h4>
        )}
        {isLoading && !isHidden && <LoadingMessage />}
        {isMenuShown && (
          <MenuItems
            items={menu.items}
            categories={menu.sort_options[selectedSortOption]?.categories}
            preSelectedItemSlug={preSelectedItemSlug}
          />
        )}
        {isMenuShown && footerContent && <footer>{footerContent}</footer>}
      </div>
    </SocialProofConfigProvider>
  );
}
